import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DollarSign, Calculator, AlertTriangle, CheckCircle, XCircle, Clock, Truck, Loader2, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_OPTIONS = [
  { label: 'Guest (Retail)', value: '0', disc: 0 },
  { label: 'Lobby (-5%)', value: '0.05', disc: 0.05 },
  { label: 'Condo (-15%)', value: '0.15', disc: 0.15 },
  { label: 'Penthouse (-20%)', value: '0.20', disc: 0.20 },
];

const ProfitCalculator = () => {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({
    vehicle: '', retail_rate: 125, hours: 3, vehicles: 1, tier_discount: '0',
    deadhead_miles: 0, driver_type: 'in_house', driver_hourly: 35, driver_flat: 0,
    ownership: 'owned', aff_rental_flat: 0, aff_rental_hourly: 0, aff_mode: 'flat',
    fuel: 40, tolls: 0, detailing: 0, misc: 0, gratuity: 0, tip_out_pct: 80,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tripLog, setTripLog] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/pricing/rate-card`).then(r => r.json()).then(d => setVehicles(d.vehicles || []));
  }, []);

  const onVehicleChange = (vid) => {
    const v = vehicles.find(x => x.vehicle_id === vid);
    if (v) {
      const isAff = v.ownership === 'affiliate';
      const sugAff = v.retail_hourly >= 400 ? v.retail_hourly * 0.55 : v.retail_hourly >= 250 ? v.retail_hourly * 0.50 : v.retail_hourly * 0.45;
      setForm(p => ({
        ...p, vehicle: vid, retail_rate: v.retail_hourly,
        ownership: isAff ? 'affiliate' : 'owned',
        driver_type: isAff ? 'affiliate' : 'in_house',
        aff_rental_flat: isAff ? Math.round(sugAff) : 0,
        hours: Math.max(p.hours, v.min_hours || 3),
      }));
    }
  };

  const calculate = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/pricing/profit-calculator`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tier_discount: parseFloat(form.tier_discount) })
      });
      setResult(await res.json());
    } catch { }
    setLoading(false);
  }, [form]);

  const saveTrip = () => {
    if (!result) return;
    const v = vehicles.find(x => x.vehicle_id === form.vehicle);
    setTripLog(p => [...p, { ...result, vehicle_name: v?.vehicle_name || 'Custom', tier: TIER_OPTIONS.find(t => t.value === form.tier_discount)?.label || 'Guest', saved_at: new Date().toISOString() }]);
  };

  const exportCSV = () => {
    const headers = ['Vehicle', 'Tier', 'Revenue', 'Expenses', 'Profit', 'Margin%', 'Profit/Hr', 'Guardrail'];
    const rows = tripLog.map(t => [t.vehicle_name, t.tier, t.gross_revenue, t.total_expenses, t.net_profit, t.margin, t.profit_per_hour, t.guardrail?.status]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'ace_trip_log.csv'; a.click();
  };

  const f = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const grColor = (s) => s === 'healthy' ? 'bg-green-50 border-green-300 text-green-800' : s === 'watch' ? 'bg-yellow-50 border-yellow-300 text-yellow-800' : 'bg-red-50 border-red-300 text-red-800';
  const grIcon = (s) => s === 'healthy' ? <CheckCircle className="h-4 w-4" /> : s === 'watch' ? <AlertTriangle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />;

  return (
    <div data-testid="profit-calculator">
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Inputs Column */}
        <div className="lg:col-span-2 space-y-3">
          {/* Vehicle & Trip */}
          <Card className="p-4 border-gray-200">
            <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Booking Inputs</h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <label className="text-[10px] text-gray-500 block mb-1">Vehicle</label>
                <Select value={form.vehicle} onValueChange={onVehicleChange}>
                  <SelectTrigger className="h-8 text-xs border-gray-200" data-testid="pc-vehicle"><SelectValue placeholder="Select vehicle..." /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map(v => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>{v.vehicle_name} — ${v.retail_hourly}/hr</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Retail Rate ($/hr)</label>
                <Input type="number" value={form.retail_rate} onChange={e => setForm(p => ({ ...p, retail_rate: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" data-testid="pc-rate" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Hours</label>
                <Input type="number" min={0.5} step={0.5} value={form.hours} onChange={e => setForm(p => ({ ...p, hours: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" data-testid="pc-hours" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Vehicles</label>
                <Input type="number" min={1} value={form.vehicles} onChange={e => setForm(p => ({ ...p, vehicles: parseInt(e.target.value) || 1 }))} className="h-8 text-xs border-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Client Tier</label>
                <Select value={form.tier_discount} onValueChange={v => setForm(p => ({ ...p, tier_discount: v }))}>
                  <SelectTrigger className="h-8 text-xs border-gray-200" data-testid="pc-tier"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIER_OPTIONS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Deadhead Miles</label>
                <Input type="number" min={0} step={5} value={form.deadhead_miles} onChange={e => setForm(p => ({ ...p, deadhead_miles: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Ownership</label>
                <div className="flex gap-1">
                  {['owned', 'affiliate'].map(o => (
                    <button key={o} onClick={() => setForm(p => ({ ...p, ownership: o }))}
                      className={`flex-1 h-8 text-[10px] font-semibold rounded border ${form.ownership === o ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {o === 'owned' ? 'In-House' : 'Affiliate'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Card>

          {/* Expenses */}
          <Card className="p-4 border-gray-200">
            <h4 className="text-xs font-bold text-gray-500 uppercase mb-3">Expense Inputs</h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Driver Type</label>
                <div className="flex gap-1">
                  {['in_house', 'affiliate'].map(d => (
                    <button key={d} onClick={() => setForm(p => ({ ...p, driver_type: d }))}
                      className={`flex-1 h-8 text-[10px] font-semibold rounded border ${form.driver_type === d ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {d === 'in_house' ? 'In-House' : 'Affiliate'}
                    </button>
                  ))}
                </div>
              </div>
              {form.driver_type === 'in_house' ? (
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">Driver $/hr</label>
                  <Input type="number" min={0} step={5} value={form.driver_hourly} onChange={e => setForm(p => ({ ...p, driver_hourly: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" data-testid="pc-driver-rate" />
                </div>
              ) : (
                <div>
                  <label className="text-[10px] text-gray-500 block mb-1">Driver Flat Fee</label>
                  <Input type="number" min={0} step={10} value={form.driver_flat} onChange={e => setForm(p => ({ ...p, driver_flat: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
                </div>
              )}
              {form.ownership === 'affiliate' && (
                <>
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">Affiliate Mode</label>
                    <div className="flex gap-1">
                      {['flat', 'hourly'].map(m => (
                        <button key={m} onClick={() => setForm(p => ({ ...p, aff_mode: m }))}
                          className={`flex-1 h-8 text-[10px] font-semibold rounded border ${form.aff_mode === m ? 'bg-black text-white border-black' : 'bg-white text-gray-600 border-gray-200'}`}>
                          {m === 'flat' ? 'Flat/Trip' : 'Per Hour'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-1">{form.aff_mode === 'flat' ? 'Affiliate Flat $' : 'Affiliate $/hr'}</label>
                    <Input type="number" min={0} step={10}
                      value={form.aff_mode === 'flat' ? form.aff_rental_flat : form.aff_rental_hourly}
                      onChange={e => setForm(p => ({ ...p, [form.aff_mode === 'flat' ? 'aff_rental_flat' : 'aff_rental_hourly']: parseFloat(e.target.value) || 0 }))}
                      className="h-8 text-xs border-gray-200" />
                  </div>
                </>
              )}
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Fuel / Gas $</label>
                <Input type="number" min={0} step={5} value={form.fuel} onChange={e => setForm(p => ({ ...p, fuel: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Tolls & Parking $</label>
                <Input type="number" min={0} step={1} value={form.tolls} onChange={e => setForm(p => ({ ...p, tolls: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Detailing $</label>
                <Input type="number" min={0} step={5} value={form.detailing} onChange={e => setForm(p => ({ ...p, detailing: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Misc $</label>
                <Input type="number" min={0} step={5} value={form.misc} onChange={e => setForm(p => ({ ...p, misc: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
            </div>

            {/* Gratuity */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 pt-3 border-t border-gray-100">
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Gratuity Collected $</label>
                <Input type="number" min={0} step={10} value={form.gratuity} onChange={e => setForm(p => ({ ...p, gratuity: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Tip-Out to Driver %</label>
                <Input type="number" min={0} max={100} step={5} value={form.tip_out_pct} onChange={e => setForm(p => ({ ...p, tip_out_pct: parseFloat(e.target.value) || 0 }))} className="h-8 text-xs border-gray-200" />
              </div>
            </div>
          </Card>

          <Button onClick={calculate} disabled={loading} className="w-full bg-red-600 hover:bg-red-700 h-10" data-testid="calculate-profit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />} Calculate Profit
          </Button>
        </div>

        {/* Results Column */}
        <div className="space-y-3">
          {result ? (
            <>
              {/* Guardrail */}
              <div className={`p-3 rounded-lg border-2 flex items-center gap-2 ${grColor(result.guardrail?.status)}`} data-testid="guardrail-status">
                {grIcon(result.guardrail?.status)}
                <span className="text-xs font-semibold">{result.guardrail?.message}</span>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 gap-2">
                <Card className="p-3 border-gray-200 text-center">
                  <p className="text-[10px] text-gray-500">Revenue</p>
                  <p className="text-lg font-bold text-black" data-testid="result-revenue">{f(result.gross_revenue)}</p>
                  <p className="text-[9px] text-gray-400">{form.hours}hrs @ {f(result.effective_rate)}/hr</p>
                </Card>
                <Card className="p-3 border-gray-200 text-center">
                  <p className="text-[10px] text-gray-500">Expenses</p>
                  <p className="text-lg font-bold text-red-600">{f(result.total_expenses)}</p>
                </Card>
                <Card className="p-3 border-gray-200 text-center">
                  <p className="text-[10px] text-gray-500">Net Profit</p>
                  <p className={`text-lg font-bold ${result.net_profit >= 0 ? 'text-green-600' : 'text-red-600'}`} data-testid="result-profit">{f(result.net_profit)}</p>
                </Card>
                <Card className="p-3 border-gray-200 text-center">
                  <p className="text-[10px] text-gray-500">Margin</p>
                  <p className="text-lg font-bold text-black" data-testid="result-margin">{result.margin}%</p>
                </Card>
                <Card className="p-3 border-gray-200 text-center">
                  <p className="text-[10px] text-gray-500">Profit/Hr</p>
                  <p className={`text-lg font-bold ${result.profit_per_hour >= 0 ? 'text-green-600' : 'text-red-600'}`}>{f(result.profit_per_hour)}</p>
                </Card>
                {result.affiliate_markup !== null && (
                  <Card className="p-3 border-gray-200 text-center">
                    <p className="text-[10px] text-gray-500">Aff. Markup</p>
                    <p className="text-lg font-bold text-blue-600">{result.affiliate_markup}%</p>
                  </Card>
                )}
                {result.gratuity > 0 && (
                  <Card className="p-3 border-gray-200 text-center col-span-2">
                    <p className="text-[10px] text-gray-500">Tip: {f(result.tip_to_driver)} to driver ({form.tip_out_pct}%) | ACE retains {f(result.ace_tip_retain)}</p>
                  </Card>
                )}
              </div>

              {/* Breakdown */}
              <Card className="p-3 border-gray-200">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase mb-2">Expense Breakdown</h4>
                <div className="space-y-1 text-xs">
                  {[
                    ['Driver Pay', result.driver_cost],
                    ['Affiliate Vehicle', result.affiliate_cost],
                    ['Fuel/Gas', form.fuel],
                    ['Deadhead Fuel', result.deadhead_fuel],
                    ['Tolls & Parking', form.tolls],
                    ['Detailing', form.detailing],
                    ['Misc', form.misc],
                  ].filter(([, v]) => v > 0).map(([label, val]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-500">{label}</span>
                      <span className="font-semibold">{f(val)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-1 border-t border-gray-200 font-bold">
                    <span>Total Expenses</span><span className="text-red-600">{f(result.total_expenses)}</span>
                  </div>
                </div>
              </Card>

              {/* Actions */}
              <div className="flex gap-2">
                <Button size="sm" onClick={saveTrip} className="flex-1 bg-green-600 hover:bg-green-700 text-xs h-8" data-testid="save-trip-btn">Save to Log</Button>
                {tripLog.length > 0 && <Button size="sm" variant="outline" onClick={exportCSV} className="text-xs h-8">Export CSV</Button>}
              </div>
            </>
          ) : (
            <Card className="p-8 border-gray-200 text-center">
              <Calculator className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-400">Select a vehicle and click Calculate to see trip profitability</p>
              <div className="mt-4 text-[10px] text-gray-400 space-y-1">
                <p className="font-semibold">Profit Guardrails:</p>
                <p className="text-green-600">Healthy: 45%+ margin — clear to confirm</p>
                <p className="text-yellow-600">Watch: 30-44% — verify expenses</p>
                <p className="text-red-600">Danger: &lt;30% — review before confirming</p>
                <p className="text-red-700">Loss: negative — do not confirm</p>
              </div>
            </Card>
          )}

          {/* Trip Log */}
          {tripLog.length > 0 && (
            <Card className="p-3 border-gray-200">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase">Trip Log ({tripLog.length})</h4>
                <button onClick={() => setTripLog([])} className="text-[10px] text-red-500 hover:underline">Clear</button>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {tripLog.map((t, i) => (
                  <div key={i} className={`flex items-center justify-between text-[10px] p-1.5 rounded ${grColor(t.guardrail?.status).replace('border-2', '')}`}>
                    <span className="font-semibold truncate mr-2">{t.vehicle_name}</span>
                    <span>{f(t.net_profit)} ({t.margin}%)</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfitCalculator;
